import { useState } from "react";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { PiGearFill } from "react-icons/pi";

import { cn } from "@bentolabs/kit/utils";

import { Button } from "shared/ui/button";
import { NumericInput } from "shared/ui/numeric-input";
import { Popover } from "shared/ui/popover";
import { Switch } from "shared/ui/switch";
import { Tab, Tabs } from "shared/ui/tabs";
import { Tooltip } from "shared/ui/tooltip";

import { useTrade } from "../context";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
  slippage: string;
  isCustomSlippage?: boolean;

  onSlippageChange: (slippage: string) => void;
  onIsCustomSlippageChange: (isCustomSlippage: boolean) => void;
};

export const SettingsPopover = ({
  open,
  onOpenChange,
  slippage,
  isCustomSlippage,
  onSlippageChange,
  onIsCustomSlippageChange,
}: Props) => {
  const [isValid, setIsValid] = useState(true);
  const { theme } = useTrade();
  const handleSlippageChange = (value: string) => {
    onIsCustomSlippageChange(false);
    onSlippageChange(value);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <Button className="size-9.5 p-0" variant="nobel/10">
          <PiGearFill className="size-5" />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        className="z-50 w-[430px] px-5 py-4"
        side="bottom"
        align="end"
        sideOffset={18}
      >
        <div className="flex justify-between text-sm text-white">
          <div className="inline-flex items-center gap-x-1">
            Max. slippage{" "}
            <Tooltip>
              <Tooltip.Trigger asChild>
                <IoIosInformationCircleOutline className="text-nobel/50 size-4" />
              </Tooltip.Trigger>
              <Tooltip.Content>
                {`Max slippage for swapping\nvia the secondary market liquidity.`}
              </Tooltip.Content>
            </Tooltip>
          </div>

          <Tabs color={theme} size="33">
            <Tab
              onClick={() => handleSlippageChange("0.05")}
              isActive={slippage === "0.05" && !isCustomSlippage}
            >
              0.05%
            </Tab>
            <Tab
              onClick={() => handleSlippageChange("0.1")}
              isActive={slippage === "0.1" && !isCustomSlippage}
            >
              0.1%
            </Tab>
            <Tab
              onClick={() => handleSlippageChange("1")}
              isActive={slippage === "1" && !isCustomSlippage}
            >
              1%
            </Tab>
            <NumericInput
              className={cn(
                "bg-nobel/10 w-20 rounded-md px-3 py-2.5 text-center text-sm",
                !isValid && "border-pink-lemonade"
              )}
              placeholder="Custom"
              max="1"
              suffix="%"
              value={isCustomSlippage ? slippage : ""}
              onValueChange={({ value, floatValue }, sourceInfo) => {
                if (sourceInfo.source !== "event") return;

                if (floatValue && floatValue >= 100) {
                  setIsValid(false);
                  return;
                }

                setIsValid(true);

                if (!value) {
                  onSlippageChange("0.01");
                  onIsCustomSlippageChange(false);
                  return;
                }

                onSlippageChange(value);
                onIsCustomSlippageChange(true);
              }}
            />
          </Tabs>
        </div>

        <div className="mt-4.5 flex justify-between text-sm text-white">
          <div className="inline-flex items-center gap-x-1">
            Mint Preferred{" "}
            <Tooltip>
              <Tooltip.Trigger asChild>
                <IoIosInformationCircleOutline className="text-nobel/50 size-4" />
              </Tooltip.Trigger>
              <Tooltip.Content>
                {`Force minting in the primary market instead of swapping\nvia secondar market liquidity.`}
              </Tooltip.Content>
            </Tooltip>
          </div>
          <Switch color={theme} />
        </div>
      </Popover.Content>
    </Popover>
  );
};
